import React, { useState } from 'react';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import styles from './ManagerCalendarWorkingHours.module.scss';
import ManagerCalendarWorkingHoursItem from './ManagerCalendarWorkingHoursItem';
import Text from '../../../../../../Components/Base/Text/Text';
import { useLazyMetaQuery } from '../../../../../../graphql/graphql';
import { useManagerContext } from '../../../../Services/ManagerContext';
import { useEffectAsync } from '../../../../../../helpers';
import Loading from '../../../../../../Components/Loading';
import moment, { Moment } from 'moment';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { getMomentDate } from '../../../../../../Utils/dates.util';
import FloatingSubmitButton from '../../../../../Base/Components/FloatingSubmitButton/FloatingSubmitButton';
import { useDisclose } from '../../../../../Modal/Hooks/useDisclose';
import ManagerCalendarWorkingHoursWeeklyModal from './ManagerCalendarWorkingHoursWeeklyModal';
import { WholeWeekValues } from './ManagerCalendarWorkingHoursWeekly';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../../Enums/Theme.enum';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

interface TransformedSchedule {
	[key: string]: {
		active: boolean;
		morning?: {
			timeFrom: Moment;
			timeTo: Moment;
		};
		afternoon?: {
			timeFrom: Moment;
			timeTo: Moment;
		};
	};
}

interface WorkingHoursDaySequence {
	timeFrom: string;
	timeTo: string;
}

interface WorkingHoursValues {
	mon?: WorkingHoursDaySequence[] | null;
	tue?: WorkingHoursDaySequence[] | null;
	wed?: WorkingHoursDaySequence[] | null;
	thu?: WorkingHoursDaySequence[] | null;
	fri?: WorkingHoursDaySequence[] | null;
	sat?: WorkingHoursDaySequence[] | null;
	sun?: WorkingHoursDaySequence[] | null;
}

const ManagerCalendarWorkingHours: React.FC = () => {
	const { workplaceId } = useManagerContext();
	const [isLoading, setIsLoading] = useState(false);
	const [getMeta] = useLazyMetaQuery();
	const { displayError } = useApiClient();
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclose(false);

	const timeRangeSchema = yup
		.object({
			timeFrom: yup.date().nullable(),
			timeTo: yup.date().nullable(),
		})
		.test(
			'both-or-none',
			t('validation.managerCalendarWorkingHours.bothTimes'),
			value => {
				const { timeFrom, timeTo } = value || {};
				const oneSet = !!timeFrom !== !!timeTo;
				return !oneSet;
			},
		)
		.test(
			'timeTo-after-timeFrom',
			t('validation.managerCalendarWorkingHours.timeToMustBeAfterTimeFrom'),
			value => {
				const { timeFrom, timeTo } = value || {};
				if (timeFrom && timeTo) {
					return new Date(timeTo) > new Date(timeFrom);
				}
				return true;
			},
		);

	const daySchema = yup
		.object({
			active: yup.boolean().required(),
			morning: timeRangeSchema,
			afternoon: timeRangeSchema,
		})
		.test(
			'required-time-when-active',
			t('validation.managerCalendarWorkingHours.activeDayWithoutTimes'),
			function (value) {
				if (!value?.active) return true;

				const morningFrom = value?.morning?.timeFrom;
				const morningTo = value?.morning?.timeTo;
				const afternoonFrom = value?.afternoon?.timeFrom;
				const afternoonTo = value?.afternoon?.timeTo;

				const morningValid = morningFrom && morningTo;
				const afternoonValid = afternoonFrom && afternoonTo;

				return Boolean(morningValid || afternoonValid);
			},
		)
		.test(
			'afternoon-after-morning',
			t('validation.managerCalendarWorkingHours.afternoonAfterMorning'),
			function (value) {
				if (!value?.active) return true;

				const morningTo = value?.morning?.timeTo
					? new Date(value.morning.timeTo)
					: null;
				const afternoonFrom = value?.afternoon?.timeFrom
					? new Date(value.afternoon.timeFrom)
					: null;

				if (morningTo && afternoonFrom) {
					return afternoonFrom > morningTo;
				}

				return true;
			},
		);

	const weekSchema = yup.object({
		mon: daySchema,
		tue: daySchema,
		wed: daySchema,
		thu: daySchema,
		fri: daySchema,
		sat: daySchema,
		sun: daySchema,
	});

	const formMethods = useForm({ resolver: yupResolver(weekSchema) });

	function transformSchedule(input: WorkingHoursValues): TransformedSchedule {
		const result: TransformedSchedule = {};

		for (const day of Object.keys(input) as DayKey[]) {
			const today = moment().format('YYYY-MM-DD');
			const entries = input[day];

			if (!entries || entries.length === 0) {
				result[day] = { active: false };
				continue;
			}

			result[day] = { active: true };

			if (entries[0]) {
				result[day].morning = {
					timeFrom: moment(`${today}T${entries[0].timeFrom}`),
					timeTo: moment(`${today}T${entries[0].timeTo}`),
				};
			}

			if (entries[1]) {
				result[day].afternoon = {
					timeFrom: moment(`${today}T${entries[1].timeFrom}`),
					timeTo: moment(`${today}T${entries[1].timeTo}`),
				};
			}
		}

		return result;
	}

	function flattenSchedule(input: TransformedSchedule): WorkingHoursValues {
		const output: WorkingHoursValues = {};
		const days: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

		for (const day of days) {
			const dayData = input[day];

			if (!dayData || !dayData.active) {
				output[day] = null;
				continue;
			}

			const sequences: WorkingHoursDaySequence[] = [];

			if (dayData.morning?.timeFrom && dayData.morning?.timeTo) {
				sequences.push({
					timeFrom: getMomentDate(dayData.morning.timeFrom).format('HH:mm:ss'),
					timeTo: getMomentDate(dayData.morning.timeTo).format('HH:mm:ss'),
				});
			}

			if (dayData.afternoon?.timeFrom && dayData.afternoon?.timeTo) {
				sequences.push({
					timeFrom: getMomentDate(dayData.afternoon.timeFrom).format(
						'HH:mm:00',
					),
					timeTo: getMomentDate(dayData.afternoon.timeTo).format('HH:mm:00'),
				});
			}

			if (sequences.length > 0) {
				output[day] = sequences;
			}
		}

		return output;
	}

	const handleLoad = async () => {
		setIsLoading(true);
		const workingHoursData = await getMeta({
			key: 'workingHours',
			workplaceId,
		});

		if (workingHoursData?.data?.meta?.value) {
			const values = transformSchedule(workingHoursData.data.meta.value);
			formMethods.reset(values);
		}
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		await handleLoad();
	}, []);

	const handleSubmit = async (vals: any) => {
		setIsLoading(true);
		const values = vals as TransformedSchedule;
		const flattenValues = flattenSchedule(values);

		try {
			const response = await apiClient.put(
				`/workplaces/${workplaceId}/working-hours`,
				flattenValues,
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('base.saved'),
				});
				await handleLoad();
			} else {
				showNotificationAlert({
					content: t('base.shared.warningBug'),
				});
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	const handleSetWholeWeek = (values: WholeWeekValues) => {
		const days: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
		days.forEach(day => {
			formMethods.setValue(`${day}.active`, true);
			formMethods.setValue(`${day}.morning.timeFrom`, values.morning?.timeFrom);
			formMethods.setValue(`${day}.morning.timeTo`, values.morning?.timeTo);
			formMethods.setValue(
				`${day}.afternoon.timeFrom`,
				values.afternoon?.timeFrom,
			);
			formMethods.setValue(`${day}.afternoon.timeTo`, values.afternoon?.timeTo);
		});
		onClose();
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.header}>
					<Text tx='base.officeHours2' className={styles.title} />
					<LinkButton
						title='component.managerCalendarWorkingHoursWeekly.setupWholeWeek'
						theme={Theme.BLUE}
						onClick={onOpen}
					/>
				</div>
				<FormWrapper formMethods={formMethods}>
					<div className={styles.list}>
						<ManagerCalendarWorkingHoursItem
							name='mon'
							title='component.time.monday'
						/>
						<ManagerCalendarWorkingHoursItem
							name='tue'
							title='component.time.tuesday'
						/>
						<ManagerCalendarWorkingHoursItem
							name='wed'
							title='component.time.wednesday'
						/>
						<ManagerCalendarWorkingHoursItem
							name='thu'
							title='component.time.thursday'
						/>
						<ManagerCalendarWorkingHoursItem
							name='fri'
							title='component.time.friday'
						/>
						<ManagerCalendarWorkingHoursItem
							name='sat'
							title='component.time.saturday'
						/>
						<ManagerCalendarWorkingHoursItem
							name='sun'
							title='component.time.sunday'
						/>
					</div>
					<div className='mt-3'>
						<FloatingSubmitButton
							onSubmit={handleSubmit}
							title='base.shared.save'
						/>
					</div>
				</FormWrapper>
			</div>
			{isOpen && (
				<ManagerCalendarWorkingHoursWeeklyModal
					isOpen
					onSubmit={handleSetWholeWeek}
					onClose={onClose}
				/>
			)}
		</>
	);
};

export default ManagerCalendarWorkingHours;
